import { type ReactElement, useCallback } from 'react';

import { cx } from '@/utils/cx';

export interface DeleteSyncedDataChecklistRow {
  entityType: string;
  label: string;
  integrationEntityId: string;
  isChecked: boolean;
}

export interface DeleteSyncedDataChecklistProps {
  rows: DeleteSyncedDataChecklistRow[];
  onToggle: (entityType: string) => void;
  disabled?: boolean;
}

interface ChecklistRowProps {
  row: DeleteSyncedDataChecklistRow;
  disabled: boolean | undefined;
  onToggle: (entityType: string) => void;
}

function ChecklistRow({
  row,
  disabled,
  onToggle,
}: ChecklistRowProps): ReactElement {
  const handleChange = useCallback(() => {
    onToggle(row.entityType);
  }, [onToggle, row.entityType]);

  return (
    <div className='flex items-center gap-2 py-1'>
      <label
        className={cx(
          'flex items-center gap-2 text-sm text-fg-secondary',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        )}
      >
        <input
          type='checkbox'
          checked={row.isChecked}
          disabled={disabled}
          data-testid={'checkbox-' + row.entityType}
          aria-label={row.label}
          value={row.entityType}
          onChange={handleChange}
        />
        {row.label}
      </label>
    </div>
  );
}

export function DeleteSyncedDataChecklist(
  props: DeleteSyncedDataChecklistProps
): ReactElement | null {
  const { rows, onToggle, disabled } = props;

  if (rows.length === 0) {
    return null;
  }

  return (
    <div data-testid='delete-synced-data-checklist' className='mt-4 space-y-2'>
      {rows.map(row => (
        <ChecklistRow
          key={row.entityType}
          row={row}
          disabled={disabled}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
}
